import { useEffect, useState } from 'react';
import { COLLAB_UNREACHABLE, isUnreachable,
  acceptInvitationById, declineInvitationById, fetchMyInvitations,
  type ApiResult, type GetToken, type GroupSummary, type MyInvitation,
} from '../api';
import { JOIN_REFUSED } from './JoinPage';
import '../pending-invitations.css';

/**
 * Invitations addressed to this teacher, on the groups page (R9–R12).
 *
 * The email is one way in; this is the other, and it needs no link. The Worker lists only
 * live invitations whose address matches the caller's explicitly verified Clerk address
 * (KTD8), so the address is the credential and the banner can offer Join and Decline outright.
 * Join is a press, never automatic: joining shows this teacher's address to the whole roster,
 * so the teacher acts.
 *
 * **It says nothing until it has something to say.** Loading, an empty list, and a failed
 * fetch all render nothing. Most teachers have no invitation on most visits, and a spinner or
 * an error above their groups for a list that is almost always empty would be noise on the
 * first screen of the product. A teacher whose address is unverified gets a 403 here, and
 * nothing is the right answer for them too — the link in their email still works.
 *
 * **One refusal sentence, the one `JoinPage` says.** The banner's accept applies every rule
 * the link does except possession (R10), and refuses with the same body for all of them (R12).
 * Rendering a different sentence here would make the banner the place to probe what the link
 * deliberately does not tell. A network failure or a 5xx decided nothing, and is kept apart.
 *
 * Decline has no confirm, unlike `/decline`: the teacher is looking at the invitation, with
 * the group's name beside the button, and a confirm would only ask what they just answered.
 *
 * The root carries `collab-pending-invitations`, which `analytics/client.ts` blocks from
 * replay — every row is a group name and a colleague's address.
 */

export interface PendingInvitationsProps {
  getToken: GetToken;
  /** A join landed. The app goes into the group it answered with. */
  onJoined: (group: GroupSummary) => void;
  /**
   * How many invitations are showing, whenever that changes. `GroupList`'s empty state points
   * at the banner rather than at New group when this is above zero.
   */
  onCountChange?: (count: number) => void;
}

/** One row's own request, so a slow join on one invitation leaves the others usable. */
type RowState = { busy: boolean; error: string | null };

const IDLE: RowState = { busy: false, error: null };

export default function PendingInvitations({ getToken, onJoined, onCountChange }: PendingInvitationsProps) {
  const [invitations, setInvitations] = useState<MyInvitation[]>([]);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  useEffect(() => {
    let live = true;
    void (async () => {
      const result = await fetchMyInvitations(getToken);
      // A failure is silent on purpose — see the comment above the component.
      if (live && result.ok) setInvitations(result.data.invitations);
    })();
    return () => { live = false; };
  }, [getToken]);

  useEffect(() => { onCountChange?.(invitations.length); }, [invitations.length, onCountChange]);

  const setRow = (id: string, state: RowState) => setRows((current) => ({ ...current, [id]: state }));

  const fail = (id: string, result: Extract<ApiResult<unknown>, { ok: false }>) =>
    setRow(id, { busy: false, error: isUnreachable(result.status) ? COLLAB_UNREACHABLE : JOIN_REFUSED });

  const remove = (id: string) => setInvitations((current) => current.filter((inv) => inv.id !== id));

  const join = async (invitation: MyInvitation) => {
    setRow(invitation.id, { busy: true, error: null });
    const result = await acceptInvitationById(getToken, invitation.id);
    if (!result.ok) { fail(invitation.id, result); return; }
    remove(invitation.id);
    onJoined(result.data.group);
  };

  const decline = async (invitation: MyInvitation) => {
    setRow(invitation.id, { busy: true, error: null });
    const result = await declineInvitationById(getToken, invitation.id);
    if (!result.ok) { fail(invitation.id, result); return; }
    // Gone, and nowhere to go: the teacher stays on their groups.
    remove(invitation.id);
  };

  if (invitations.length === 0) return null;

  return (
    <section className="collab-pending-invitations" aria-label="Invitations">
      <h3>{invitations.length === 1 ? 'An invitation is waiting for you' : 'Invitations waiting for you'}</h3>
      <ul>
        {invitations.map((invitation) => {
          const { busy, error } = rows[invitation.id] ?? IDLE;
          return (
            <li key={invitation.id} className="pending-invitation">
              <div className="pending-invitation-text">
                <p className="pending-invitation-group">{invitation.groupName}</p>
                <p className="meta">
                  {/* Null when the inviting admin has since left; the invitation still stands. */}
                  {invitation.invitedByEmail ? `Invited by ${invitation.invitedByEmail}` : 'You are invited to join'}
                </p>
                {error && <p className="error" role="alert">{error}</p>}
              </div>
              <div className="pending-invitation-actions">
                <button
                  type="button"
                  className="primary"
                  aria-label={`Join ${invitation.groupName}`}
                  disabled={busy}
                  onClick={() => { void join(invitation); }}
                >
                  Join
                </button>
                <button
                  type="button"
                  aria-label={`Decline ${invitation.groupName}`}
                  disabled={busy}
                  onClick={() => { void decline(invitation); }}
                >
                  Decline
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
